/**
 * PDF receipt generators for tire storage operations.
 * - generateDepositReceipt: emanet (giriş) makbuzu — at storage check-in
 * - generateDeliveryReceipt: teslim (çıkış) makbuzu — at storage check-out
 *
 * Both embed a QR code containing the storage ID for fast lookup.
 * Uses Roboto (TTF) so Turkish characters (ğ, ş, ı, İ, ç, ö, ü) render correctly —
 * jsPDF's built-in Helvetica is WinAnsi-only and mangles them.
 */
import jsPDF from "jspdf";
import { fmtMoney } from "./currency";

const COLOR_BG = [15, 23, 42];
// Tüm PDF çıktılarının (makbuz, etiket, rapor) üst BAŞLIK BANDI. Slate-600 —
// eski slate-900'a göre belirgin şekilde daha açık, beyaz başlık metniyle
// kontrastı hâlâ yüksek. Vurgu bloklarında (ör. "KALAN TUTAR") koyu ton
// korunur, bu yüzden ayrı bir sabit tutuluyor.
const COLOR_HEADER_BG = [71, 85, 105];
const COLOR_MUTED = [100, 116, 139];
const COLOR_LABEL = [64, 82, 110];
const COLOR_BORDER = [203, 213, 225];
const COLOR_TEXT = [15, 23, 42];
// Dark-gray label tone used on the large karekodsuz etiket for field labels
// (TEL, PLAKA, MARKA, SEZON, …). Slate-800 — daha belirgin bir koyu gri,
// gövde metniyle (siyaha yakın) arada net bir ton farkı bırakır.
const COLOR_LABEL_DARK = [30, 41, 59];
// Darker, thicker divider line for the section break under the header.
const COLOR_DIVIDER = [120, 130, 145];

const CURRENCY_SYMBOL = {
  TRY: "TL",
  USD: "$",
  EUR: "€",
};

const FONT_NAME = "Roboto";

// Module-level cache: fetch + base64-encode each TTF only once per page load.
let fontDataPromise = null;

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunkSize)
    );
  }
  return btoa(binary);
}

async function loadFontData() {
  if (!fontDataPromise) {
    fontDataPromise = (async () => {
      const [regularBuf, boldBuf] = await Promise.all([
        fetch("/fonts/Roboto-Regular.ttf").then((r) => {
          if (!r.ok) throw new Error(`Roboto-Regular fetch failed: ${r.status}`);
          return r.arrayBuffer();
        }),
        fetch("/fonts/Roboto-Bold.ttf").then((r) => {
          if (!r.ok) throw new Error(`Roboto-Bold fetch failed: ${r.status}`);
          return r.arrayBuffer();
        }),
      ]);
      return {
        regular: arrayBufferToBase64(regularBuf),
        bold: arrayBufferToBase64(boldBuf),
      };
    })().catch((e) => {
      // Reset on failure so a later attempt can retry.
      fontDataPromise = null;
      throw e;
    });
  }
  return fontDataPromise;
}

async function registerFonts(doc) {
  const { regular, bold } = await loadFontData();
  doc.addFileToVFS("Roboto-Regular.ttf", regular);
  doc.addFont("Roboto-Regular.ttf", FONT_NAME, "normal");
  doc.addFileToVFS("Roboto-Bold.ttf", bold);
  doc.addFont("Roboto-Bold.ttf", FONT_NAME, "bold");
  doc.setFont(FONT_NAME, "normal");
}

function svgToPng(svgEl, size = 240) {
  return new Promise((resolve, reject) => {
    if (!svgEl) {
      resolve(null);
      return;
    }
    try {
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    } catch (e) {
      reject(e);
    }
  });
}

function seasonLabel(s) {
  if (s === "yaz") return "YAZ";
  if (s === "kis") return "KIŞ";
  if (s === "dort_mevsim") return "4 MEVSİM";
  return (s || "").toUpperCase();
}

// Bir emanet kaydındaki lastik çeşitleri. Eski kayıtlarda items yoksa
// top-level alanlardan tek kalemlik liste üretilir.
function tireItems(storage) {
  if (storage.items && storage.items.length) return storage.items;
  return [{
    brand: storage.brand,
    size: storage.size,
    dot: storage.dot,
    tread_depth_mm: storage.tread_depth_mm,
    quantity: storage.quantity,
    season: storage.season,
  }];
}

// Çoklu lastik listesini makbuza satır satır yazar; her kalem
// "1) Marka Ebat — 4 adet - KIŞ (DOT 2823)" biçimindedir.
function itemsBlock(doc, W, y, items) {
  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...COLOR_LABEL);
  doc.text("LASTİKLER", 24, y);
  y += 11;

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_TEXT);
  items.forEach((it, i) => {
    const nameSize = [it.brand, it.size].filter(Boolean).join(" ");
    let line = `${i + 1}) ${nameSize} — ${it.quantity} adet - ${seasonLabel(it.season)}`;
    if (it.dot) line += ` (DOT ${it.dot})`;
    const wrapped = doc.splitTextToSize(line, W - 48);
    doc.text(wrapped, 24, y);
    y += wrapped.length * 11 + 2;
  });
  return y + 6;
}

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Çıktılarda gösterilecek iletişim bilgileri.
 *
 * Değerler Ayarlar > "Çıktılarda Görünecek İletişim Bilgileri"nden gelir;
 * boş bırakılan alanlar için backend mevcut yönetici/işletme bilgisine
 * DÜŞMÜŞ hâlde gönderir (server.py: resolve_receipt_contact) → burada ek
 * kural işletilmez, yalnızca boş olanlar atlanır.
 */
function contactLines(contact) {
  const c = contact || {};
  return {
    phone: (c.phone || "").trim(),
    email: (c.email || "").trim(),
    website: (c.website || "").trim(),
    address: (c.address || "").trim(),
  };
}

/** Tek satırda "•" ile ayrılmış iletişim özeti (adres hariç). */
function contactInline(contact) {
  const c = contactLines(contact);
  return [c.phone, c.email, c.website].filter(Boolean).join("  •  ");
}

function header(doc, W, title, tenantName, contact) {
  doc.setFillColor(...COLOR_HEADER_BG);
  doc.rect(0, 0, W, 70, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont(FONT_NAME, "bold");
  const tenantTitle = (tenantName || "LASTİK OTELİ").toLocaleUpperCase("tr-TR").trim();
  doc.text(tenantTitle, 24, 30, { maxWidth: W - 48 });

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(FONT_NAME, "normal");
  const titleText = (title || "").trim();
  doc.text(titleText, 24, 50, { maxWidth: W - 48 });

  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  const stamp = new Date().toLocaleString("tr-TR");
  doc.text(stamp, W - 24, 50, { align: "right" });

  // İletişim özeti — başlık bandının alt satırı. Hiçbir alan doluysa çizilmez
  // ve makbuzun mevcut düzeni aynen korunur.
  const inline = contactInline(contact);
  if (inline) {
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(inline, 24, 63, { maxWidth: W - 48 });
  }
}

function infoRow(doc, y, k, v, x = 24) {
  const label = String(k || "").toLocaleUpperCase("tr-TR").trim();
  const value = String(v == null || v === "" ? "-" : v).trim();

  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...COLOR_LABEL);
  doc.text(label, x, y, { maxWidth: 180, align: "left" });

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COLOR_TEXT);
  doc.text(value, x, y + 9, { maxWidth: 180, align: "left" });

  return y + 26;
}

function divider(doc, W, y) {
  doc.setDrawColor(...COLOR_BORDER);
  doc.line(24, y, W - 24, y);
  return y + 14;
}

async function buildBaseDoc({ storage, tenantName, qrSvgEl, title, kind, contact }) {
  const doc = new jsPDF({ unit: "pt", format: "a5" });
  await registerFonts(doc);
  const W = doc.internal.pageSize.getWidth();

  header(doc, W, title, tenantName, contact);

  let y = 96;

  doc.setTextColor(...COLOR_TEXT);
  doc.setFontSize(11);
  doc.setFont(FONT_NAME, "bold");
  const receiptNo = `Makbuz No: ${storage.id.slice(0, 8).toUpperCase()}`.trim();
  doc.text(receiptNo, 24, y, { maxWidth: W - 48 });

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(9);
  const dateLabel = kind === "delivery" ? "Teslim Tarihi" : "Giriş Tarihi";
  const dateValue = kind === "delivery" ? storage.check_out_date : storage.check_in_date;
  const dateLine = `${dateLabel}: ${fmtDate(dateValue)}`.trim();
  doc.text(dateLine, 24, y + 14, { maxWidth: W - 48 });

  y += 36;

  // Firma adresi — QR'ın soluna sığacak genişlikte, yalnızca ayarlıysa.
  const address = contactLines(contact).address;
  if (address) {
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    const wrapped = doc.splitTextToSize(address, W - 150);
    doc.text(wrapped, 24, y);
    y += wrapped.length * 10 + 4;
    doc.setTextColor(...COLOR_TEXT);
  }

  y = divider(doc, W, y);

  try {
    const qrPng = await svgToPng(qrSvgEl, 240);
    if (qrPng) {
      doc.addImage(qrPng, "PNG", W - 110, 86, 86, 86);
      doc.setFontSize(7);
      doc.setTextColor(...COLOR_MUTED);
      const qrText = storage.id.slice(0, 12).trim();
      doc.text(qrText, W - 110, 180);
    }
  } catch {
    /* QR embedding failed — proceed without */
  }

  return { doc, W, y };
}

/**
 * Emanet listesi PDF — Raporlar > Emanetler tabından dışa aktarılır.
 * A4 yatay düzen, çok sayfalı tablo. Sütunlar emanet durumuna göre değişmez;
 * boş alanlar "-" ile doldurulur.
 */
export async function generateStoragesListPdf({
  rows,
  tenantName,
  defaultCurrency,
  statusLabel,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  await registerFonts(doc);
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const currencySymbol = CURRENCY_SYMBOL[defaultCurrency] || defaultCurrency || "TRY";

  // Column layout — widths sum to W - 2*margin (24 + 24 = 48).
  const margin = 24;
  const cols = [
    { key: "customer_name", label: "MÜŞTERİ", w: 110 },
    { key: "plate", label: "PLAKA", w: 60 },
    { key: "brand_size", label: "MARKA / ÖLÇÜ", w: 110 },
    { key: "quantity", label: "ADET", w: 36, align: "right" },
    { key: "season", label: "SEZON", w: 50 },
    { key: "location", label: "DEPO / RAF", w: 100 },
    { key: "check_in_date", label: "GİRİŞ", w: 70 },
    { key: "status", label: "DURUM", w: 60 },
    { key: "fee", label: `ÜCRET (${currencySymbol})`, w: 60, align: "right" },
    { key: "paid_amount", label: `ÖDENEN (${currencySymbol})`, w: 65, align: "right" },
  ];
  // Stretch the first column to absorb remaining width if any.
  const totalW = cols.reduce((s, c) => s + c.w, 0);
  const free = W - margin * 2 - totalW;
  if (free !== 0) cols[0].w += free;

  const rowH = 22;
  const headerH = 22;
  const headerY0 = 70; // first-page header takes 0..60
  let pageNo = 1;

  const drawPageHeader = () => {
    doc.setFillColor(...COLOR_HEADER_BG);
    doc.rect(0, 0, W, 56, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(14);
    const tenantTitle = (tenantName || "LASTİK OTELİ")
      .toLocaleUpperCase("tr-TR")
      .trim();
    doc.text(tenantTitle, margin, 26, { maxWidth: W - margin * 2 });

    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("EMANET LİSTESİ", margin, 44);

    doc.setFontSize(8);
    const stamp = new Date().toLocaleString("tr-TR");
    doc.text(stamp, W - margin, 26, { align: "right" });
    if (statusLabel) {
      doc.text(`Filtre: ${statusLabel}`, W - margin, 44, { align: "right" });
    }
  };

  const drawTableHeader = (y) => {
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, W - margin * 2, headerH, "F");
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.5);
    doc.rect(margin, y, W - margin * 2, headerH);

    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...COLOR_LABEL);
    let x = margin;
    for (const c of cols) {
      const tx = c.align === "right" ? x + c.w - 4 : x + 4;
      doc.text(c.label, tx, y + headerH - 7, { align: c.align || "left", maxWidth: c.w - 6 });
      x += c.w;
    }
    return y + headerH;
  };

  const drawFooter = () => {
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(`Sayfa ${pageNo}`, W - margin, H - 16, { align: "right" });
    doc.text(`Toplam ${rows.length} kayıt`, margin, H - 16);
  };

  const statusOf = (s) =>
    s.status === "depoda" ? "Depoda" : s.status === "teslim_edildi" ? "Teslim" : (s.status || "-");

  const rowToCells = (s) => ({
    customer_name: s.customer_name || "-",
    plate: s.plate || "-",
    brand_size: [s.brand, s.size].filter(Boolean).join(" / ") || "-",
    quantity: String(s.quantity ?? 0),
    season: seasonLabel(s.season),
    location: `${s.warehouse_name || "-"} / ${s.location || "-"}`,
    check_in_date:
      s.check_in_date
        ? new Date(s.check_in_date).toLocaleDateString("tr-TR")
        : "-",
    status: statusOf(s),
    fee: fmtMoney(s.fee, defaultCurrency, { maximumFractionDigits: 0 }),
    paid_amount: fmtMoney(s.paid_amount, defaultCurrency, { maximumFractionDigits: 0 }),
  });

  drawPageHeader();
  let y = drawTableHeader(headerY0);

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_TEXT);

  for (let i = 0; i < rows.length; i++) {
    const cells = rowToCells(rows[i]);
    // Zebra stripe
    if (i % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, W - margin * 2, rowH, "F");
    }
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.25);
    doc.line(margin, y + rowH, W - margin, y + rowH);

    let x = margin;
    for (const c of cols) {
      const tx = c.align === "right" ? x + c.w - 4 : x + 4;
      const text = String(cells[c.key] ?? "-");
      doc.text(text, tx, y + rowH - 8, {
        align: c.align || "left",
        maxWidth: c.w - 6,
      });
      x += c.w;
    }
    y += rowH;

    // Page break
    if (y + rowH > H - 36 && i < rows.length - 1) {
      drawFooter();
      doc.addPage();
      pageNo += 1;
      drawPageHeader();
      y = drawTableHeader(headerY0);
      doc.setFont(FONT_NAME, "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLOR_TEXT);
    }
  }
  drawFooter();

  doc.save(`emanet-listesi-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Satış/Stok raporu PDF'i (şartname #23).
 *
 * `generateStoragesListPdf` ile AYNI altyapıyı kullanır (aynı font kaydı, aynı
 * başlık/zebra/sayfa-kırma mantığı) — yeni bir PDF altyapısı kurulmaz. Sadece
 * kolon şeması ve alt toplam bloğu farklıdır.
 */
export async function generateSalesStockReportPdf({
  rows,
  totals,
  collections,
  tenantName,
  defaultCurrency,
  filterLabel,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  await registerFonts(doc);
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const currencySymbol = CURRENCY_SYMBOL[defaultCurrency] || defaultCurrency || "TRY";

  const margin = 24;
  const cols = [
    { key: "name", label: "ÜRÜN", w: 150 },
    { key: "product_type", label: "TİP", w: 70 },
    { key: "warehouse_name", label: "DEPO", w: 80 },
    { key: "quantity", label: "STOK", w: 42, align: "right" },
    { key: "critical_quantity", label: "KRİTİK", w: 46, align: "right" },
    { key: "entry_quantity", label: "GİRİŞ", w: 42, align: "right" },
    { key: "sale_quantity", label: "SATIŞ", w: 42, align: "right" },
    { key: "return_quantity", label: "İADE", w: 42, align: "right" },
    { key: "usage_quantity", label: "KULLANIM", w: 56, align: "right" },
    { key: "sales_gross", label: `SATIŞ (${currencySymbol})`, w: 72, align: "right" },
    { key: "profit_net", label: `KÂR (${currencySymbol})`, w: 66, align: "right" },
  ];
  const totalW = cols.reduce((s, c) => s + c.w, 0);
  const free = W - margin * 2 - totalW;
  if (free !== 0) cols[0].w += free;

  const rowH = 20;
  const headerH = 22;
  const headerY0 = 70;
  let pageNo = 1;

  const TYPE_LABELS = { lastik: "Lastik", jant: "Jant", diger: "Diğer Malzeme" };

  const drawPageHeader = () => {
    doc.setFillColor(...COLOR_HEADER_BG);
    doc.rect(0, 0, W, 56, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(14);
    doc.text((tenantName || "LASTİK OTELİ").toLocaleUpperCase("tr-TR").trim(), margin, 26, {
      maxWidth: W - margin * 2,
    });
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(10);
    doc.text("SATIŞ / STOK RAPORU", margin, 44);
    doc.setFontSize(8);
    doc.text(new Date().toLocaleString("tr-TR"), W - margin, 26, { align: "right" });
    if (filterLabel) doc.text(`Filtre: ${filterLabel}`, W - margin, 44, { align: "right" });
  };

  const drawTableHeader = (y) => {
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, W - margin * 2, headerH, "F");
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.5);
    doc.rect(margin, y, W - margin * 2, headerH);
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_LABEL);
    let x = margin;
    for (const c of cols) {
      const tx = c.align === "right" ? x + c.w - 4 : x + 4;
      doc.text(c.label, tx, y + headerH - 7, { align: c.align || "left", maxWidth: c.w - 6 });
      x += c.w;
    }
    return y + headerH;
  };

  const drawFooter = () => {
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(`Sayfa ${pageNo}`, W - margin, H - 16, { align: "right" });
    doc.text(`Toplam ${rows.length} ürün`, margin, H - 16);
  };

  const cell = (r) => ({
    name: r.name || "-",
    product_type: TYPE_LABELS[r.product_type] || r.product_type || "-",
    warehouse_name: r.warehouse_name || "-",
    quantity: String(r.quantity ?? 0),
    critical_quantity: String(r.critical_quantity ?? 0),
    entry_quantity: String(r.entry_quantity ?? 0),
    sale_quantity: String(r.sale_quantity ?? 0),
    return_quantity: String(r.return_quantity ?? 0),
    usage_quantity: String(r.usage_quantity ?? 0),
    sales_gross: fmtMoney(r.sales_gross, defaultCurrency, { maximumFractionDigits: 0 }),
    profit_net: fmtMoney(r.profit_net, defaultCurrency, { maximumFractionDigits: 0 }),
  });

  drawPageHeader();
  let y = drawTableHeader(headerY0);
  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_TEXT);

  for (let i = 0; i < rows.length; i++) {
    const cells = cell(rows[i]);
    if (i % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, W - margin * 2, rowH, "F");
    }
    // Kritik stok satırını görsel olarak işaretle
    if (rows[i].is_critical) {
      doc.setFillColor(254, 226, 226);
      doc.rect(margin, y, W - margin * 2, rowH, "F");
    }
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.25);
    doc.line(margin, y + rowH, W - margin, y + rowH);
    let x = margin;
    for (const c of cols) {
      const tx = c.align === "right" ? x + c.w - 4 : x + 4;
      doc.text(String(cells[c.key] ?? "-"), tx, y + rowH - 7, {
        align: c.align || "left",
        maxWidth: c.w - 6,
      });
      x += c.w;
    }
    y += rowH;

    if (y + rowH > H - 90 && i < rows.length - 1) {
      drawFooter();
      doc.addPage();
      pageNo += 1;
      drawPageHeader();
      y = drawTableHeader(headerY0);
      doc.setFont(FONT_NAME, "normal");
      doc.setFontSize(8);
      doc.setTextColor(...COLOR_TEXT);
    }
  }

  // --- Alt toplam + tahsilat kırılımı ---
  if (y + 80 > H - 40) {
    drawFooter();
    doc.addPage();
    pageNo += 1;
    drawPageHeader();
    y = headerY0;
  }
  y += 12;
  doc.setDrawColor(...COLOR_DIVIDER);
  doc.setLineWidth(1);
  doc.line(margin, y, W - margin, y);
  y += 16;

  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_TEXT);
  doc.text("TOPLAMLAR", margin, y);
  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(8.5);
  const t = totals || {};
  const summary = [
    `Ürün çeşidi: ${t.product_count ?? 0}`,
    `Stok: ${t.stock_quantity ?? 0} adet`,
    `Kritik: ${t.critical_count ?? 0}`,
    `Giriş: ${t.entry_quantity ?? 0}`,
    `Satış: ${t.sale_quantity ?? 0}`,
    `İade: ${t.return_quantity ?? 0}`,
    `Kullanım: ${t.usage_quantity ?? 0}`,
  ];
  y += 14;
  doc.text(summary.join("   •   "), margin, y, { maxWidth: W - margin * 2 });
  y += 14;
  doc.text(
    [
      `Satış tutarı (KDV dahil): ${fmtMoney(t.sales_gross, defaultCurrency)}`,
      `Satış (KDV hariç): ${fmtMoney(t.sales_net, defaultCurrency)}`,
      `Brüt kâr: ${fmtMoney(t.profit_net, defaultCurrency)}`,
      `Tahsil edilen: ${fmtMoney(t.collected, defaultCurrency)}`,
    ].join("   •   "),
    margin,
    y,
    { maxWidth: W - margin * 2 }
  );

  if (collections && collections.length) {
    y += 18;
    doc.setFont(FONT_NAME, "bold");
    doc.text("TAHSİLAT ŞEKLİ KIRILIMI", margin, y);
    doc.setFont(FONT_NAME, "normal");
    y += 14;
    const METHOD_LABELS = {
      nakit: "Nakit",
      sanal_pos: "Sanal Pos",
      fiziki_pos: "Fiziki Pos",
      eft: "EFT",
      dis_pos: "Dış Pos",
    };
    doc.text(
      collections
        .map(
          (c) =>
            `${METHOD_LABELS[c.method] || c.method}: ${fmtMoney(
              c.total,
              defaultCurrency
            )} (${c.count})`
        )
        .join("   •   "),
      margin,
      y,
      { maxWidth: W - margin * 2 }
    );
  }

  drawFooter();
  doc.save(`satis-stok-raporu-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * QR etiket PDF — termal etiket yazıcı için lastik üstüne yapıştırılabilir
 * boyutta tek sayfalık etiket. Format 50 × 80 mm portrait (142pt × 227pt),
 * Argox/Zebra/Brother rulolarında yaygın olarak kullanılan ölçüdür. Üst
 * yarıda QR, alt yarıda plaka / müşteri / adet / marka-ölçü / sezon /
 * depo-raf bilgileri yer alır.
 */
export async function generateQrLabel({ storage, qrSvgEl, contact }) {
  const LABEL_W = 142; // 50mm
  const LABEL_H = 227; // 80mm
  const doc = new jsPDF({ unit: "pt", format: [LABEL_W, LABEL_H] });
  await registerFonts(doc);

  const margin = 6;
  // QR — top, centered. 100pt (~35mm) is comfortably scannable and leaves
  // ~110pt of vertical room below for 8 data lines.
  const qrSize = 100;
  const qrX = (LABEL_W - qrSize) / 2;
  const qrY = margin;

  try {
    const qrPng = await svgToPng(qrSvgEl, 320);
    if (qrPng) {
      doc.addImage(qrPng, "PNG", qrX, qrY, qrSize, qrSize);
    }
  } catch {
    /* QR embedding failed — proceed without */
  }

  // Separator under QR
  let y = qrY + qrSize + 6;
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.5);
  doc.line(margin, y, LABEL_W - margin, y);
  y += 10;

  const centerX = LABEL_W / 2;
  const textW = LABEL_W - margin * 2;
  doc.setTextColor(...COLOR_TEXT);

  // DEPO / RAF ETİKETTE YOK — depo yerleşimi sonradan değişebiliyor, basılı
  // etiketteki değer yanıltıcı olurdu. Boşalan yer punto büyütmeye ayrıldı.
  //
  // Satırlar önce TOPLANIR, sonra tek bir ölçek (`scale`) ile en büyük sığan
  // boyutta çizilir. Ölçek, GERÇEK sarma davranışı ölçülerek seçilir
  // (`splitTextToSize`), bu yüzden taşma matematiksel olarak imkânsızdır.
  const items = tireItems(storage);
  /** @type {{text: string, size: number, bold: boolean}[]} */
  const lines = [
    { text: storage.plate || "-", size: 12, bold: true },
    { text: storage.customer_name || "-", size: 10, bold: true },
  ];

  if (items.length <= 1) {
    if (storage.brand) lines.push({ text: storage.brand, size: 9, bold: false });
    if (storage.size) lines.push({ text: storage.size, size: 9, bold: false });
    lines.push({ text: `${storage.quantity || 0} adet`, size: 9, bold: false });
    lines.push({ text: `Sezon: ${seasonLabel(storage.season)}`, size: 9, bold: false });
  } else {
    // Çoklu lastik — her çeşit tek satırda; marka/ebat/adet zorunlu.
    items.forEach((it) => {
      lines.push({
        text: `${[it.brand, it.size].filter(Boolean).join(" ")} • ${it.quantity} adet`,
        size: 9,
        bold: false,
      });
    });
  }

  const blockTop = y;
  const availTotal = LABEL_H - margin - blockTop;
  // Satır aralığı puntoyla birlikte büyür (punto + %22 nefes payı).
  const gapFor = (size) => size * 1.22;

  // ---------------------------------------------------------------------
  // İletişim şeridi (etiketin en altı)
  // ---------------------------------------------------------------------
  // Kaynak: Ayarlar > "Çıktılarda Görünecek İletişim Bilgileri". Boş alanlar
  // için backend zaten yönetici/işletme bilgisine düşmüş hâlini gönderiyor
  // (server.py: resolve_receipt_contact) → burada ek fallback kuralı yok.
  //
  // ÖNCELİK: telefon > e-posta > web sitesi > adres. Etiket 50 mm geniş
  // olduğu için adres neredeyse hiçbir zaman tek satıra sığmaz; SARMA
  // YASAK olduğundan sığmayan aday sessizce atlanır (kesilmiş adres
  // göstermektense hiç göstermemek daha okunaklı).
  const CONTACT_FONT = 6.5;
  const CONTACT_PAD = 5;        // şerit ile ana blok arasındaki nefes payı
  const CONTACT_MAX_LINES = 3;
  // Ana metin bu ölçeğin altına inerse iletişim satırı feda edilir.
  const MAIN_SCALE_COMFORT = 0.85;

  const cl = contactLines(contact);
  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(CONTACT_FONT);
  const contactAll = [cl.phone, cl.email, cl.website, cl.address]
    .filter(Boolean)
    // Tek satıra sığmayanı alma — şeritte sarma istemiyoruz.
    .filter((t) => doc.splitTextToSize(t, textW).length === 1);
  // Kullanıcı isteği: en azından telefon + e-posta görünsün.
  const contactMin = Math.min(2, contactAll.length);
  const contactHeightFor = (n) =>
    n ? CONTACT_PAD + n * gapFor(CONTACT_FONT) : 0;
  // Karekod ile alttaki metin bloğu arasında BİR SATIR boşluk bırakılır.
  // Sabit pt değil, ilk satırın ölçeklenmiş yüksekliği kadar → punto büyüse de
  // küçülse de boşluk daima "1 satır" olarak görünür.
  const leadGap = (scale) => gapFor(lines[0].size * scale);

  const wrapCount = (ln, scale) => {
    doc.setFont(FONT_NAME, ln.bold ? "bold" : "normal");
    doc.setFontSize(ln.size * scale);
    return doc.splitTextToSize(String(ln.text ?? "-"), textW).length;
  };

  // Ölçek 1'deki sarma sayıları referans alınır: büyütme YENİ sarma
  // ÜRETMEMELİ. Aksi hâlde "Michelin 205/55 R16 • 4" / "adet" gibi ortadan
  // bölünmüş satırlar oluşuyor ve etiket daha büyük puntoya rağmen daha kötü
  // okunuyor. Sığmayan bir aday, yüksekliğe uysa bile reddedilir.
  const baseWraps = lines.map((ln) => wrapCount(ln, 1));

  /** Aday ölçek için toplam yükseklik; yeni sarma varsa null. */
  const measure = (scale) => {
    let total = leadGap(scale);
    for (let i = 0; i < lines.length; i++) {
      const n = wrapCount(lines[i], scale);
      if (n > baseWraps[i]) return null;
      total += n * gapFor(lines[i].size * scale);
    }
    return total;
  };

  // 1.00 = mevcut puntolar. Üst sınır 2.2× (tek satırlık etiketlerde bile
  // okunabilirlik dışına çıkmasın), ALT sınır 0.55 — iletişim şeridi için yer
  // açmak gerektiğinde punto küçültülebilir. Küçültmek yeni sarma ÜRETEMEZ
  // (baseWraps ölçek 1'de hesaplandı), bu yüzden aşağı arama güvenlidir.
  const bestScale = (avail) => {
    for (let s = 2.2; s >= 0.55; s -= 0.05) {
      const h = measure(s);
      if (h !== null && h <= avail) return s;
    }
    return 0.55; // son çare: en küçük punto (pratikte ulaşılmaz)
  };

  // İletişim satırı sayısı ile ana metin puntosu arasındaki denge: mümkün
  // olduğunca çok iletişim satırı göster, ama ana metin MAIN_SCALE_COMFORT
  // altına düşerse en düşük öncelikliyi (adres → web) bırak.
  let contactUsed = contactAll.slice(0, CONTACT_MAX_LINES);
  let scale = bestScale(availTotal - contactHeightFor(contactUsed.length));
  while (scale < MAIN_SCALE_COMFORT && contactUsed.length > contactMin) {
    contactUsed = contactUsed.slice(0, -1);
    scale = bestScale(availTotal - contactHeightFor(contactUsed.length));
  }
  y = blockTop + leadGap(scale);

  lines.forEach((ln) => {
    const size = ln.size * scale;
    doc.setFont(FONT_NAME, ln.bold ? "bold" : "normal");
    doc.setFontSize(size);
    const wrapped = doc.splitTextToSize(String(ln.text ?? "-"), textW);
    doc.text(wrapped, centerX, y, { align: "center" });
    y += wrapped.length * gapFor(size);
  });

  // İletişim şeridi — etiketin ALT kenarına sabitlenir (ana metnin uzunluğuna
  // göre kaymaz), üstünde ince bir ayraç.
  if (contactUsed.length) {
    const stripTop = LABEL_H - margin - contactUsed.length * gapFor(CONTACT_FONT);
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.4);
    doc.line(margin + 12, stripTop - CONTACT_PAD, LABEL_W - margin - 12,
             stripTop - CONTACT_PAD);

    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(CONTACT_FONT);
    doc.setTextColor(...COLOR_MUTED);
    let cy = stripTop + CONTACT_FONT * 0.85;
    contactUsed.forEach((t) => {
      doc.text(t, centerX, cy, { align: "center" });
      cy += gapFor(CONTACT_FONT);
    });
    doc.setTextColor(...COLOR_TEXT);
  }

  doc.save(`qr-etiket-${storage.id.slice(0, 8)}.pdf`);
}

/**
 * Karekodsuz büyük etiket — 100 × 80 mm yatay (283pt × 227pt). Termal etiket
 * yazıcılarda 4" × 3" rulolarında yaygın olarak kullanılan ölçüye yakın.
 * Üst blokta BÜYÜK harflerle isim soyisim / telefon / plaka yer alır;
 * alt blokta emanet meta bilgileri (tarih, sezon, marka, adet, ölçü, depo,
 * raf) tek bakışta okunacak şekilde grid biçiminde dizilir.
 */
export async function generateLargeLabel({
  storage,
  tenantName,
  // GERİYE UYUMLULUK: `contact` verilmezse eski tenantEmail/tenantPhone
  // parametreleri kullanılır. Yeni çağrılar `contact` göndermelidir.
  tenantEmail,
  tenantPhone,
  contact,
  defaultCurrency = "TRY",
}) {
  const LABEL_W = 283; // 100mm
  const LABEL_H = 227; // 80mm
  const doc = new jsPDF({ unit: "pt", format: [LABEL_W, LABEL_H], orientation: "landscape" });
  await registerFonts(doc);

  const margin = 14;
  const centerX = LABEL_W / 2;

  const items = tireItems(storage);
  const multi = items.length > 1;

  // Tüm metin renkleri varsayılan: siyah/lacivert ana ton.
  doc.setTextColor(...COLOR_TEXT);

  // Helper — alan başlığı çiz (TEL / PLAKA / MARKA / SEZON / …).
  const drawFieldLabel = (text, x, y, opts = {}) => {
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(opts.fontSize ?? 10);
    doc.setTextColor(...COLOR_LABEL_DARK);
    doc.text(text, x, y, opts.options);
  };

  // Helper — alan değeri çiz (telefon / plaka / Petlas / …).
  const drawFieldValue = (text, x, y, opts = {}) => {
    doc.setFont(FONT_NAME, opts.bold ? "bold" : "normal");
    doc.setFontSize(opts.fontSize ?? 12);
    doc.setTextColor(...COLOR_TEXT);
    doc.text(text, x, y, opts.options);
  };

  // --- Dikey ortalama için: önce toplam içerik yüksekliğini tahmin et ve
  //     etikete oranla başlangıç y'sini hesapla. Footer LABEL_H − 6'ya
  //     mutlak yerleştiği için contentH'ye dahil edilmez; üst içerik bu
  //     mutlak alt sınırın üzerinde dikey ortalanır.
  const headerHeight = 14 + 13 + 11; // tenant + Lastik Oteli + iletişim
  const dividerGapTop = 6;
  const dividerGapBottom = 22; // müşteri adının çizgiyle çakışmaması için yeterli boşluk
  const customerExtra = 8;       // müşteri adı satırı ile TEL/PLAKA arası ek nefes payı
  const customerH = 19 + customerExtra;
  const telPlakaRowH = 16;
  const gridGapTop = 12;
  const gridGapExtra = 14;       // TEL/PLAKA → grid arasında genişletilmiş boşluk
  const gridH = 16 * 3;          // DEPO/RAF kaldırıldı → en uzun sütun 3 satır
  const footerReserve = 14;      // alt URL için ayrılan minimum boşluk
  const contentH =
    headerHeight + dividerGapTop + dividerGapBottom + customerH +
    telPlakaRowH + gridGapTop + gridGapExtra + gridH;
  // Üstte biraz daha fazla nefes payı bırak (kullanıcı talebi: "yukarıdan biraz
  // boşluk bırak, dikey ortalı"). Footer alanını rezerve ettikten sonra
  // kalan slack'in ~55%'ini üste ayır, kalanı altta nefes payı olarak bırak.
  const slack = Math.max(0, LABEL_H - footerReserve - contentH);
  // Çoklu lastikte alt blok daha uzun olacağından içeriği yukarıdan başlat.
  const topPadding = multi ? 12 : Math.max(18, Math.floor(slack * 0.55));
  let y = topPadding;

  // --- 1) Üst blok (ortalı): işletme adı / Lastik Oteli / iletişim ---
  const tenantTitle = (tenantName || "").trim();
  if (tenantTitle) {
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(13);
    doc.setTextColor(...COLOR_TEXT);
    const titleLines = doc.splitTextToSize(tenantTitle, LABEL_W - margin * 2);
    doc.text(titleLines, centerX, y, { align: "center" });
    y += titleLines.length * 14;
  }

  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(12);
  doc.setTextColor(...COLOR_TEXT);
  doc.text("Lastik Oteli", centerX, y, { align: "center" });
  y += 13;

  // Ayarlardan gelen çıktı iletişim bilgileri; yoksa eski parametrelere düşer.
  const lc = contactLines(contact);
  const contactParts = [
    lc.email || (tenantEmail || "").trim(),
    lc.phone || (tenantPhone || "").trim(),
  ].filter(Boolean);
  if (contactParts.length) {
    doc.setFont(FONT_NAME, "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR_TEXT);
    doc.text(contactParts.join("  •  "), centerX, y, { align: "center" });
    y += 11;
  }

  // --- 2) Daha belirgin ayraç çizgisi ---
  y += dividerGapTop;
  doc.setDrawColor(...COLOR_DIVIDER);
  doc.setLineWidth(1.1);
  doc.line(margin, y, LABEL_W - margin, y);
  // Müşteri adının çizgiye değmemesi için belirgin boşluk
  y += dividerGapBottom;

  // --- 3) Müşteri adı (BÜYÜK, BOLD, ortalı) ---
  const customerUpper = (storage.customer_name || "-")
    .toLocaleUpperCase("tr-TR")
    .trim();
  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(18);
  doc.setTextColor(...COLOR_TEXT);
  const nameLines = doc.splitTextToSize(customerUpper, LABEL_W - margin * 2);
  doc.text(nameLines, centerX, y, { align: "center" });
  // Müşteri adından sonra ekstra nefes payı: alt blok aşağı doğru kayar,
  // ekranda görünen toplam tasarım daha dengeli durur.
  y += nameLines.length * 19 + 8;

  // Grid sütun geometrisi — iki sütun da merkeze yakın konumlandırıldı
  // (kullanıcı talebi: "her iki sütunu birlikte biraz sola kaydır").
  const leftColX = margin + 25;
  const leftLabelW = 56;
  const rightColX = LABEL_W / 2 - 8;
  const rightLabelW = 50;
  const rowH = 16;

  // --- 4) Telefon + Plaka satırı — etiketsiz, ikisi birlikte ortalı çizilir.
  const phoneText = (storage.customer_phone || "-").trim();
  const plateText = (storage.plate || "-").toLocaleUpperCase("tr-TR").trim();

  doc.setFont(FONT_NAME, "bold");
  doc.setFontSize(14);
  doc.setTextColor(...COLOR_TEXT);
  const phoneW = doc.getTextWidth(phoneText);
  const plateW = doc.getTextWidth(plateText);
  const pairGap = 32;
  const pairStartX = (LABEL_W - (phoneW + pairGap + plateW)) / 2;
  doc.text(phoneText, pairStartX, y);
  doc.text(plateText, pairStartX + phoneW + pairGap, y);

  // TEL/PLAKA satırı ile alt grid arasındaki dikey aralığı belirgin tut
  // (kullanıcı talebi: "satır arasını aç").
  y += gridGapTop + 14;

  // --- 5) Alt blok: 2 sütunlu meta grid ---
  // DEPO / RAF ETİKETTE YOK — depo yerleşimi sonradan değişebiliyor (Emanet
  // Detayı ekranından güncellenebiliyor), basılı etiketteki değer yanıltıcı
  // olurdu. Boşalan alan punto büyütmeye ayrıldı: aşağıdaki `pickScale`
  // GERÇEK metin genişliklerini (`getTextWidth`) ve satır yüksekliklerini
  // ölçerek sığan EN BÜYÜK ölçeği seçer → taşma olamaz.
  const seasonStr = seasonLabel(storage.season);
  const qtyStr = `${storage.quantity ?? 0}`;
  const checkInStr = storage.check_in_date
    ? new Date(storage.check_in_date).toLocaleDateString("tr-TR")
    : "-";
  const brandStr = storage.brand || "-";
  const sizeStr = storage.size || "-";

  // Ücret satırı gösterilecek mi? Grid'in alt sınırı buna göre belirlenir.
  const remainingFee = Math.max(0, (storage.fee || 0) - (storage.paid_amount || 0));
  const willShowFee = remainingFee > 0 || (multi && (storage.fee || 0) > 0);
  const gridTop = y;
  // Ücret satırı grid'in ALTINA, ona göre konumlanır (sabit rezerv değil):
  // `contentEndY = max(leftY, rightY) + 8`, üstten LABEL_H-footerReserve-4'e
  // klemplenir. Bu yüzden grid'in alt sınırı = ücret satırının kendi yüksekliği
  // (12pt) kadar yukarı. Eskiden 18 alınıyordu ve büyütme hiç tetiklenmiyordu.
  const gridBottom = LABEL_H - footerReserve - (willShowFee ? 12 : 4);
  const gridAvailH = gridBottom - gridTop;

  /** Ölçek adaylarını büyükten küçüğe dener; ilk sığanı döner. */
  const pickScale = (probe, max = 1.8) => {
    for (let sc = max; sc > 1; sc -= 0.05) {
      const cfg = probe(sc);
      if (cfg) return cfg;
    }
    return probe(1);
  };

  const textW2 = (text, size, bold) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    return doc.getTextWidth(String(text ?? "-"));
  };

  let leftY;
  let rightY;

  if (!multi) {
    // TEKLİ LASTİK — Sol sütun: EMANET TARİHİ (label üst, değer alt), SEZON,
    // ADET. Sağ sütun: MARKA / ÖLÇÜ.
    const leftAvailW = rightColX - leftColX - 8;
    const rightAvailW = LABEL_W - rightColX - margin;
    // ADET, boşalan sağ sütuna alındı → iki sütun dengelenir, en uzun sütun
    // kısalır ve aynı alanda daha büyük punto kullanılabilir.
    const leftInline = [["SEZON", seasonStr]];
    const rightInline = [["MARKA", brandStr], ["ÖLÇÜ", sizeStr], ["ADET", qtyStr]];

    const probe = (sc) => {
      const labelFS = 10 * sc;
      const valueFS = 12 * sc;
      const rH = rowH * sc;
      const stackH = 13 * sc;
      // Dikey: iki sütundan uzunu sığmalı.
      const hLeft = stackH + rH * (1 + leftInline.length);
      const hRight = rH * rightInline.length;
      if (Math.max(hLeft, hRight) > gridAvailH) return null;
      // Yatay: satır içi etiket + değer aynı satırda, çakışmamalı.
      const inlineLabelW = (rows, availW) => {
        const lw =
          Math.max(...rows.map(([k]) => textW2(k, labelFS, true))) + 6 * sc;
        const vw = Math.max(...rows.map(([, v]) => textW2(v, valueFS, false)));
        return lw + vw <= availW ? lw : null;
      };
      const lw = inlineLabelW(leftInline, leftAvailW);
      const rw = inlineLabelW(rightInline, rightAvailW);
      if (lw == null || rw == null) return null;
      // Yığın alan (EMANET TARİHİ) tek satırda kalmalı.
      if (textW2("EMANET TARİHİ", labelFS, true) > leftAvailW) return null;
      if (textW2(checkInStr, valueFS, false) > leftAvailW) return null;
      return { labelFS, valueFS, rH, stackH, lw, rw };
    };

    // Ölçek 1'de bile ölçüm sığmazsa (ör. çok uzun marka adı) mevcut
    // sabitlere dönülür → bugünkü çıktı davranışı korunur.
    const cfg = pickScale(probe) || {
      labelFS: 10, valueFS: 12, rH: rowH, stackH: 13,
      lw: leftLabelW, rw: rightLabelW,
    };

    drawFieldLabel("EMANET TARİHİ", leftColX, y, { fontSize: cfg.labelFS });
    drawFieldValue(checkInStr, leftColX, y + cfg.stackH, { fontSize: cfg.valueFS });
    leftY = y + cfg.stackH + cfg.rH;

    leftInline.forEach(([k, v]) => {
      drawFieldLabel(k, leftColX, leftY, { fontSize: cfg.labelFS });
      drawFieldValue(v, leftColX + cfg.lw, leftY, { fontSize: cfg.valueFS });
      leftY += cfg.rH;
    });

    rightY = y;
    rightInline.forEach(([k, v]) => {
      drawFieldLabel(k, rightColX, rightY, { fontSize: cfg.labelFS });
      drawFieldValue(v, rightColX + cfg.rw, rightY, {
        fontSize: cfg.valueFS,
        options: { maxWidth: LABEL_W - (rightColX + cfg.rw) - margin },
      });
      rightY += cfg.rH;
    });
  } else {
    // ÇOKLU LASTİK — sol sütunda yalnızca EMANET TARİHİ (depo/raf kaldırıldı);
    // sağ sütunda tüm lastik çeşitleri (marka + ebat + adet zorunlu) listelenir.
    // Sol blok sola yaslanır, liste sütunu genişletilir ki lastik satırları
    // ikinci satıra düşmesin.
    const mLeftX = margin;
    const mListX = 104;
    const leftMaxW = mListX - mLeftX - 6;

    // Depo/raf satırlarının boşalttığı yer tarih alanının puntosuna aktarılır;
    // ölçek gerçek metin genişliğiyle sınanır, taşma olamaz.
    const dateCfg = pickScale((sc) => {
      const labelFS = 10 * sc;
      const valueFS = 13 * sc;
      if (labelFS + 4 + valueFS * 1.25 > gridAvailH) return null;
      if (textW2("EMANET TARİHİ", labelFS, true) > leftMaxW) return null;
      if (textW2(checkInStr, valueFS, false) > leftMaxW) return null;
      return { labelFS, valueFS };
    }) || { labelFS: 10, valueFS: 13 };

    drawFieldLabel("EMANET TARİHİ", mLeftX, y, { fontSize: dateCfg.labelFS });
    drawFieldValue(checkInStr, mLeftX, y + dateCfg.labelFS + 4, {
      fontSize: dateCfg.valueFS,
      options: { maxWidth: leftMaxW },
    });
    leftY = y + dateCfg.labelFS + 4 + dateCfg.valueFS * 1.25;

    // Sağ sütun — LASTİKLER listesi (genişletilmiş sütun)
    const listX = mListX;
    const listW = LABEL_W - listX - margin;
    drawFieldLabel("LASTİKLER", listX, y, { fontSize: 10 });
    const listStartY = y + 14;

    const lineTexts = items.map(
      (it) =>
        `${[it.brand, it.size].filter(Boolean).join(" ")} • ${it.quantity} ad • ${seasonLabel(it.season)}`
    );

    // Kalan yüksekliğe (footer ve olası kalan-ödeme satırının üstüne) sığacak
    // en büyük punto/aralığı seç.
    // Ücret satırı çoklu etikette her zaman gösterildiğinden (fee>0) yer ayır.
    const hasFeeLine = (storage.fee || 0) > 0;
    const bottomLimit = LABEL_H - footerReserve - (hasFeeLine ? 18 : 4);
    const availH = bottomLimit - listStartY;
    let chosen = { fontSize: 6, gap: 8 };
    for (let fs = 13; fs >= 6; fs -= 0.5) {
      const gap = fs + 2.5;
      doc.setFont(FONT_NAME, "normal");
      doc.setFontSize(fs);
      const totalLines = lineTexts.reduce(
        (n, t) => n + doc.splitTextToSize(t, listW).length,
        0
      );
      if (totalLines * gap <= availH) {
        chosen = { fontSize: fs, gap };
        break;
      }
    }

    let listY = listStartY;
    doc.setTextColor(...COLOR_TEXT);
    lineTexts.forEach((t) => {
      doc.setFont(FONT_NAME, "normal");
      doc.setFontSize(chosen.fontSize);
      const wrapped = doc.splitTextToSize(t, listW);
      doc.text(wrapped, listX, listY);
      listY += wrapped.length * chosen.gap;
    });
    rightY = listY;
  }

  // --- 5.5) Ücret satırı — 14pt bold, ortalı.
  // Kalan ödeme varsa "Ücret(kalan ödeme)"; yoksa çoklu etikette toplam ücret
  // ("Ücret") gösterilir. Tekli etiketin mevcut davranışı korunur (yalnızca
  // kalan ödeme varken görünür).
  // `remainingFee` grid'in alt sınırı için yukarıda hesaplandı; tekrar
  // hesaplanmaz ki iki yer birbirinden ayrışmasın.
  let feeLineText = null;
  if (remainingFee > 0) {
    feeLineText = `Ücret(kalan ödeme): ${fmtMoney(remainingFee, defaultCurrency)}`;
  } else if (multi && (storage.fee || 0) > 0) {
    feeLineText = `Ücret: ${fmtMoney(storage.fee, defaultCurrency)}`;
  }
  // İçeriğin altına yerleştir; footer'a çakışmaması için üst sınıra klempele.
  const contentEndY = Math.min(
    Math.max(leftY, rightY) + 8,
    LABEL_H - footerReserve - 4
  );
  if (feeLineText) {
    doc.setFont(FONT_NAME, "bold");
    doc.setFontSize(14);
    doc.setTextColor(...COLOR_TEXT);
    doc.text(feeLineText, centerX, contentEndY, { align: "center" });
  }

  // --- 6) Alt orta: sabit URL — etiketin en alt kenarına oturtuldu
  // (kullanıcı talebi: "URL'yi en aşağıya indir"). Çıktıda yazıcı kesim
  // payı bırakacak şekilde alt kenardan 6pt yukarıda.
  // Ayarlarda web sitesi girilmişse o gösterilir; yoksa mevcut sabit adres.
  const FOOTER_URL = lc.website || "https://lastikoteli.esnekyazilim.com.tr/";
  // İçeriğin bitişinden değil, etiket alt kenarından referansla konum:
  void leftY; void rightY;
  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR_MUTED);
  doc.text(FOOTER_URL, centerX, LABEL_H - 6, { align: "center" });

  doc.save(`etiket-${storage.id.slice(0, 8)}.pdf`);
}

/**
 * Karekodlu büyük etiket — 100 × 100 mm (283pt × 283pt).
 *
 * 10 × 8 etiketin (generateLargeLabel) tasarımını TEMEL ALIR ve korur:
 * üstte işletme başlığı + iletişim, ortada BÜYÜK müşteri adı, altında
 * telefon/plaka, en altta meta grid ve ücret satırı.
 *
 * 10 × 8'den FARKLARI (şartname #6):
 *   • Karekod (emanet detay sayfasına götüren URL)
 *   • DEPO / RAF bilgisi geri geliyor (bu etiket rafta duran lastiğin üstünde)
 *   • Ayarlar > "Çıktılarda Görünecek İletişim Bilgileri": telefon/e-posta
 *     başlıkta, adres + web sitesi altta — 10 × 8 ile AYNI fallback mantığı
 *
 * PUNTOLAR: fazladan 56pt yükseklik metinlere aktarılır. Grid ölçeği gerçek
 * metin genişlikleri (`getTextWidth`) ve satır yükseklikleri ölçülerek
 * seçilir; sığmayan aday reddedilir → taşma ve satır bölünmesi oluşamaz.
 */
export async function generateLargeQrLabel({
  storage,
  tenantName,
  tenantEmail,
  tenantPhone,
  contact,
  qrSvgEl,
  defaultCurrency = "TRY",
}) {
  const LABEL_W = 283; // 100mm
  const LABEL_H = 283; // 100mm
  const doc = new jsPDF({ unit: "pt", format: [LABEL_W, LABEL_H] });
  await registerFonts(doc);

  const margin = 14;
  const centerX = LABEL_W / 2;
  const fullW = LABEL_W - margin * 2;
  const bottomLimit = LABEL_H - margin;

  const items = tireItems(storage);
  const multi = items.length > 1;
  const lc = contactLines(contact);
  const headerContact = [
    lc.email || (tenantEmail || "").trim(),
    lc.phone || (tenantPhone || "").trim(),
  ].filter(Boolean).join("  •  ");

  doc.setTextColor(...COLOR_TEXT);

  const widthOf = (text, size, bold) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    return doc.getTextWidth(String(text == null ? "-" : text));
  };
  const wrap = (text, size, bold, w) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    return doc.splitTextToSize(String(text == null ? "-" : text), w);
  };
  const put = (text, x, yy, size, { bold, align, maxWidth, color } = {}) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...(color || COLOR_TEXT));
    const opts = {};
    if (align) opts.align = align;
    if (maxWidth) opts.maxWidth = maxWidth;
    doc.text(String(text == null ? "-" : text), x, yy, opts);
  };

  // ---------------------------------------------------------------------
  // 1) KAREKOD — SAĞ ÜST köşe.
  // Üst köşeye alınması bilinçli: karekod alt bölgede olsaydı meta grid ile
  // aynı 72pt'lik dikey bandı paylaşmak zorunda kalır, grid'e ~68pt kalır ve
  // puntolar 5-6pt'ye düşerdi. Üstte, başlık metninin yanındaki ölü alanı
  // kullanıyor → grid TAM GENİŞLİK ve tüm kalan yüksekliği alıyor.
  // Boyut 62pt (~22mm): kısa bir URL için telefon kameralarıyla rahat okunur
  // ve gereğinden büyük değil. Her fazladan pt doğrudan meta grid'in
  // puntosundan gidiyor, bu yüzden ölçü bilinçli olarak dar tutuldu.
  // ---------------------------------------------------------------------
  const qrSize = 62;
  const qrX = LABEL_W - margin - qrSize;
  const qrY = margin + 2;
  let hasQr = false;
  try {
    const qrPng = await svgToPng(qrSvgEl, 320);
    if (qrPng) {
      doc.addImage(qrPng, "PNG", qrX, qrY, qrSize, qrSize);
      hasQr = true;
    }
  } catch {
    /* QR gömülemedi — etiket karekodsuz üretilir, diğer bilgiler korunur */
  }

  // ---------------------------------------------------------------------
  // 2) Üst blok — işletme adı / Lastik Oteli / iletişim.
  // Karekod varsa onun SOLUNDAKİ alana ortalanır, yoksa tüm genişliğe.
  // ---------------------------------------------------------------------
  const headRight = hasQr ? qrX - 8 : LABEL_W - margin;
  const headW = headRight - margin;
  const headCenter = margin + headW / 2;

  let y = margin + 16;
  const tenantTitle = (tenantName || "").trim();
  if (tenantTitle) {
    const lines = wrap(tenantTitle, 14, true, headW);
    put(lines.join("\n"), headCenter, y, 14, { bold: true, align: "center", maxWidth: headW });
    y += lines.length * 16;
  }
  put("Lastik Oteli", headCenter, y, 12, { bold: true, align: "center" });
  y += 14;
  if (headerContact) {
    const lines = wrap(headerContact, 8.5, false, headW);
    put(lines.join("\n"), headCenter, y, 8.5, { align: "center", maxWidth: headW });
    y += lines.length * 10;
  }

  // Ayraç, karekodun ALTINDAN geçer ki iki blok çakışmasın.
  y = Math.max(y + 6, hasQr ? qrY + qrSize + 10 : y + 6);
  doc.setDrawColor(...COLOR_DIVIDER);
  doc.setLineWidth(1.1);
  doc.line(margin, y, LABEL_W - margin, y);
  // DİKKAT: jsPDF'te `y` TABAN ÇİZGİSİDİR, yazının üstü değil. Ayraçtan
  // sonraki boşluk, gelecek yazının ÜST UZANTISINDAN (ascent ≈ punto × 0.78)
  // büyük olmalı; aksi hâlde büyük puntolu müşteri adı çizginin üstüne biner.
  const NAME_FS = 22;
  y += NAME_FS * 0.78 + 5;

  // ---------------------------------------------------------------------
  // 3) Müşteri adı (BÜYÜK) + telefon/plaka — tam genişlik, ortalı.
  // 10 × 8 etiketteki görsel dil korunuyor.
  // ---------------------------------------------------------------------
  const customerUpper = (storage.customer_name || "-")
    .toLocaleUpperCase("tr-TR").trim();
  const nameLines = wrap(customerUpper, 22, true, fullW);
  put(nameLines.join("\n"), centerX, y, 22, { bold: true, align: "center", maxWidth: fullW });
  y += nameLines.length * 24 + 4;

  const phoneText = (storage.customer_phone || "-").trim();
  const plateText = (storage.plate || "-").toLocaleUpperCase("tr-TR").trim();
  const phoneW = widthOf(phoneText, 16, true);
  const plateW = widthOf(plateText, 16, true);
  const pairGap = 34;
  const pairStartX = (LABEL_W - (phoneW + pairGap + plateW)) / 2;
  put(phoneText, pairStartX, y, 16, { bold: true });
  put(plateText, pairStartX + phoneW + pairGap, y, 16, { bold: true });
  y += 12;

  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.6);
  doc.line(margin, y, LABEL_W - margin, y);
  y += 10;

  // ---------------------------------------------------------------------
  // 4) Alt sabit blok için yer ayır: ücret → adres → web sitesi
  // ---------------------------------------------------------------------
  const website = lc.website || "";
  const address = lc.address || "";
  const remaining = Math.max(0, (storage.fee || 0) - (storage.paid_amount || 0));
  const feeText = remaining > 0
    ? `Ücret(kalan ödeme): ${fmtMoney(remaining, defaultCurrency)}`
    : ((storage.fee || 0) > 0
        ? `Ücret: ${fmtMoney(storage.fee, defaultCurrency)}`
        : null);
  // Grid'in okunabilir kalması ADRESTEN önceliklidir: uzun müşteri adı gibi
  // durumlarda üst blok büyüyüp grid'e çok az yer kalabiliyor. Bu eşiğin
  // altına düşülürse adres satırı çıkarılır (telefon/e-posta zaten başlıkta,
  // web sitesi altta kalmaya devam eder).
  const MIN_GRID_H = 62;
  // 2+ çeşit lastikte alt web sitesi satırı GÖSTERİLMEZ: liste zaten uzun,
  // boşalan yer grid puntosuna aktarılır (ölçek seçimi bunu otomatik kullanır).
  const showWebsite = !multi;
  const footerHeight = (withAddress, addrLines) =>
    (showWebsite ? 12 : 0) + (feeText ? 18 : 0) +
    (withAddress && addrLines.length ? addrLines.length * 10 + 2 : 0);

  let addressLines = address ? wrap(address, 8.5, false, fullW) : [];
  const gridTop = y;
  let footerH = footerHeight(true, addressLines);
  if (bottomLimit - footerH - 4 - gridTop < MIN_GRID_H && addressLines.length) {
    addressLines = [];
    footerH = footerHeight(false, addressLines);
  }
  const gridBottom = bottomLimit - footerH - 4;
  const availH = gridBottom - gridTop;

  // ---------------------------------------------------------------------
  // 5) Meta grid — TAM GENİŞLİK, 2 sütun.
  // Sol: EMANET TARİHİ (etiket üstte) + DEPO + RAF   → bu etiketin ayırt
  // edici bilgisi. Sağ: tekli lastikte MARKA/ÖLÇÜ/ADET/SEZON, çokluda çeşit
  // listesi. Alanlar SATIR İÇİ (etiket solda, değer sağda) dizilir; yığın
  // dizilim satır başına iki kat yer yiyor ve puntoyu yarıya düşürüyordu.
  // ---------------------------------------------------------------------
  // Sütun genişlikleri: tekli lastikte eşit; ÇOKLUDA sol sütun daraltılır
  // (yalnızca kısa TARİH/DEPO/RAF taşır) ve lastik listesine geniş alan
  // bırakılır → satırlar sarmaz, punto belirgin şekilde büyür.
  const colGap = 14;
  const leftColX = margin;

  const depoStr = storage.warehouse_name || "-";
  const rafStr = storage.location || "-";
  const checkInStr = storage.check_in_date
    ? new Date(storage.check_in_date).toLocaleDateString("tr-TR")
    : "-";

  const leftInline = [["DEPO", depoStr], ["RAF", rafStr]];
  const rightInline = multi
    ? null
    : [
        ["MARKA", storage.brand || "-"],
        ["ÖLÇÜ", storage.size || "-"],
        ["ADET", `${storage.quantity == null ? 0 : storage.quantity}`],
        ["SEZON", seasonLabel(storage.season)],
      ];
  const tyreLines = multi
    ? items.map((it) =>
        `${[it.brand, it.size].filter(Boolean).join(" ")} • ${it.quantity} ad • ${seasonLabel(it.season)}`)
    : [];

  /**
   * Sığan EN BÜYÜK ölçek. Kısıtlar ayrı ele alınır:
   *   • ETİKET başlıkları kısa ve sabittir → sütuna sığmazsa ölçek reddedilir.
   *   • DEĞERLER kalan genişlikte sarılabilir → sarılan satır sayısı
   *     yüksekliğe dahil edilir (reddetme yerine zarif bozunma). Böylece uzun
   *     bir marka adı tüm grid'i minik puntoya düşürmez.
   */
  const probe = (sc) => {
    const labelFS = 8.5 * sc;
    const valueFS = 12 * sc;
    const rowH = 15 * sc;
    const stackH = 11 * sc;
    const labelGap = 5 * sc;

    const lw = (rows) =>
      Math.max(...rows.map(([k]) => widthOf(k, labelFS, true))) + labelGap;
    // SOL SÜTUNDA ETİKET YOK: "DEPO"/"RAF" başlıkları kaldırıldı, yalnızca
    // değerler yazılıyor → değerler sütunun TAMAMINI kullanır.
    const leftLabelW = 0;

    // SOL SÜTUNUN İHTİYACI — TEK KAYNAK.
    // DİKKAT: bu ölçü hem sütun genişliğini belirlemek hem de yeterlilik
    // kontrolü için kullanılır. Önceden ikisi ayrı ayrı yazılıydı ve biri
    // `labelGap`'i eklerken diğeri eklemiyordu; aradaki fark yüzünden ölçek
    // hep reddediliyor, çoklu etikette punto gereksiz küçülüyordu.
    const leftNeed = Math.max(
      widthOf("EMANET TARİHİ", labelFS, true) + labelGap,
      widthOf(checkInStr, valueFS, false),
      // Etiketsiz: yalnızca değerin kendi genişliği
      ...leftInline.map(([, v]) => widthOf(v, valueFS, false))
    );

    // Tekli lastikte iki sütun da alan bilgisi taşır → eşit bölünür.
    // ÇOKLUDA sol sütun yalnızca kısa TARİH/DEPO/RAF taşır → ihtiyacı kadar
    // yer alır, ARTAN GENİŞLİK lastik listesine gider (liste sarmadan daha
    // büyük puntoya çıkabilir).
    const leftColW = multi
      ? Math.min(Math.max(leftNeed + 2, 60), fullW * 0.5)
      : (fullW - colGap) / 2;
    const rightColW = fullW - colGap - leftColW;

    if (leftNeed > leftColW) return null;
    const rightLabel = rightInline
      ? Math.max(...rightInline.map(([k]) => widthOf(k, labelFS, true)))
      : widthOf("LASTİKLER", labelFS, true);
    if (rightLabel + labelGap >= rightColW) return null;
    const inlineH = (rows, labelW, w) =>
      rows.reduce(
        (t, [, v]) => t + wrap(v, valueFS, false, w - labelW).length * rowH,
        0
      );
    // Sol: tarih yığın (etiket + değer), ardından satır içi DEPO/RAF
    const hLeft =
      stackH + wrap(checkInStr, valueFS, false, leftColW).length * rowH +
      inlineH(leftInline, leftLabelW, leftColW);

    let hRight;
    let rightLabelW = 0;
    if (rightInline) {
      rightLabelW = lw(rightInline);
      hRight = inlineH(rightInline, rightLabelW, rightColW);
    } else {
      hRight =
        stackH +
        tyreLines.reduce((t, v) => t + wrap(v, valueFS, false, rightColW).length * rowH, 0);
    }
    if (Math.max(hLeft, hRight) > availH) return null;
    return { labelFS, valueFS, rowH, stackH, leftLabelW, rightLabelW,
             leftColW, rightColW };
  };

  // Taban 0.4'e kadar inilir: çok çeşitli emanetlerde (ör. 6 lastik tipi)
  // punto küçülür ama grid DAİMA ayrılan alana sığar → ücret/adres/web
  // satırlarının üzerine binmez. Yerleşimin bozulmaması puntodan önce gelir.
  let cfg = null;
  for (let sc = 1.8; sc >= 0.4; sc -= 0.05) {
    cfg = probe(sc);
    if (cfg) break;
  }
  if (!cfg) {
    // 0.4'te bile sığmıyorsa (etikete sığmayacak kadar çok kalem) en küçük
    // ölçekle çizilir; değerler maxWidth ile kırpıldığı için yatay taşma yok.
    const sc = 0.4;
    const lcw = multi ? 84 : (fullW - colGap) / 2;
    cfg = {
      labelFS: 8.5 * sc, valueFS: 12 * sc, rowH: 15 * sc, stackH: 11 * sc,
      leftLabelW: widthOf("DEPO", 8.5 * sc, true) + 5 * sc,
      rightLabelW: widthOf("SEZON", 8.5 * sc, true) + 5 * sc,
      leftColW: lcw, rightColW: fullW - colGap - lcw,
    };
  }

  const { leftColW, rightColW } = cfg;
  const rightColX = margin + leftColW + colGap;

  // --- Sol sütun
  let ly = gridTop + cfg.stackH;
  put("EMANET TARİHİ", leftColX, ly - cfg.stackH + cfg.labelFS, cfg.labelFS,
      { bold: true, color: COLOR_LABEL_DARK });
  put(checkInStr, leftColX, ly + cfg.rowH * 0.65, cfg.valueFS, { maxWidth: leftColW });
  ly += cfg.stackH + wrap(checkInStr, cfg.valueFS, false, leftColW).length * cfg.rowH;

  // SOL SÜTUN: "DEPO" / "RAF" BAŞLIKLARI YAZILMAZ — yalnızca değerler.
  // Başlıkların bıraktığı yer değerlere aktarılır (bkz. probe: leftLabelW = 0).
  leftInline.forEach(([k, v]) => {
    const vw = leftColW;

    if (k === "RAF") {
      // RAF bu etiketin ARANAN bilgisidir: sol sütunda KALAN tüm alana
      // taşmadan sığabilecek EN BÜYÜK puntoyla yazılır. Uzun değerler önce
      // SATIR KAYDIRILIR, yine sığmazsa punto düşer.
      //
      // DİKKAT: `ly` TABAN ÇİZGİSİDİR; punto büyüdükçe yazı YUKARI doğru
      // uzar ve üstteki depo satırına binerdi. Bu yüzden büyüme, normal
      // puntolu yazının ÜST KENARINDAN (topEdge) aşağı doğru hesaplanır ve
      // taban çizgisi ona göre kaydırılır. Alt sınır `gridBottom` → alt
      // bloğa (ücret/adres/web) taşamaz. Genişlik sınırı kendi sütunu →
      // sağ sütuna giremez.
      const topEdge = ly - cfg.valueFS * 0.78;
      const roomH = gridBottom - topEdge;
      let best = null;
      for (let t = cfg.valueFS * 4; t >= cfg.valueFS; t -= 0.5) {
        const lines = wrap(v, t, true, vw);
        const widest = Math.max(...lines.map((l) => widthOf(l, t, true)));
        if (widest <= vw && lines.length * t * 1.1 <= roomH) {
          best = { t, lines };
          break;
        }
      }
      if (best) {
        put(best.lines.join("\n"), leftColX, topEdge + best.t * 0.78,
            best.t, { bold: true, maxWidth: vw });
        ly = topEdge + best.lines.length * best.t * 1.1 + 2;
        return;
      }
      // Hiçbir ölçek sığmadıysa eski davranış: normal puntoda sar.
    }

    put(v, leftColX, ly, cfg.valueFS, { maxWidth: vw });
    ly += wrap(v, cfg.valueFS, false, vw).length * cfg.rowH;
  });

  // --- Sağ sütun
  let ry = gridTop + cfg.stackH;
  if (rightInline) {
    ry = gridTop + cfg.rowH * 0.65;
    rightInline.forEach(([k, v]) => {
      put(k, rightColX, ry, cfg.labelFS, { bold: true, color: COLOR_LABEL_DARK });
      const vw = rightColW - cfg.rightLabelW;
      put(v, rightColX + cfg.rightLabelW, ry, cfg.valueFS, { maxWidth: vw });
      ry += wrap(v, cfg.valueFS, false, vw).length * cfg.rowH;
    });
  } else {
    put("LASTİKLER", rightColX, gridTop + cfg.labelFS, cfg.labelFS,
        { bold: true, color: COLOR_LABEL_DARK });
    ry = gridTop + cfg.stackH + cfg.rowH * 0.65;
    tyreLines.forEach((t) => {
      put(t, rightColX, ry, cfg.valueFS, { maxWidth: rightColW });
      ry += wrap(t, cfg.valueFS, false, rightColW).length * cfg.rowH;
    });
  }

  // ---------------------------------------------------------------------
  // 6) Alt sabit blok
  // ---------------------------------------------------------------------
  let fy = bottomLimit - footerH + 12;
  if (feeText) {
    put(feeText, centerX, fy, 13, { bold: true, align: "center" });
    fy += 8;
  }
  if (addressLines.length) {
    put(addressLines.join("\n"), centerX, fy + 8, 8.5,
        { align: "center", maxWidth: fullW, color: COLOR_MUTED });
  }
  if (showWebsite) {
    put(website || "https://lastikoteli.esnekyazilim.com.tr/", centerX,
        bottomLimit - 1, 8, { align: "center", color: COLOR_MUTED });
  }

  doc.save(`etiket-10x10-${storage.id.slice(0, 8)}.pdf`);
}

/**
 * 10 × 10 cm — Karekodlu MÜŞTERİ / RAF etiketi.
 *
 * `generateLargeQrLabel` ile AYNI üst bloğu kullanır (firma adı, "Lastik
 * Oteli", iletişim satırı, sağ üstte karekod, altında ayraç). Farkı: ayracın
 * altında YALNIZCA müşteri adı ve raf/konum bulunur — lastik/ürün, tarih,
 * ücret gibi hiçbir alan GÖSTERİLMEZ. Amaç rafta uzaktan okunabilirlik.
 *
 * PUNTO: raf/konum, etikette kalan boşluğa TAŞMADAN sığacak en büyük puntoyla
 * yazılır. Ölçek büyükten küçüğe denenir; her adımda hem satır genişliği hem
 * toplam yükseklik kontrol edilir (uzun değerler önce sarılır, yine sığmazsa
 * punto düşer). Müşteri adı da aynı yöntemle sığdırılır.
 */
export async function generateLargeQrLocationLabel({
  storage,
  tenantName,
  tenantEmail,
  tenantPhone,
  contact,
  qrSvgEl,
}) {
  const LABEL_W = 283; // 100mm
  const LABEL_H = 283; // 100mm
  const doc = new jsPDF({ unit: "pt", format: [LABEL_W, LABEL_H] });
  await registerFonts(doc);

  const margin = 14;
  const centerX = LABEL_W / 2;
  const fullW = LABEL_W - margin * 2;
  const bottomLimit = LABEL_H - margin;

  const lc = contactLines(contact);
  const headerContact = [
    lc.email || (tenantEmail || "").trim(),
    lc.phone || (tenantPhone || "").trim(),
  ].filter(Boolean).join("  •  ");

  doc.setTextColor(...COLOR_TEXT);

  const wrap = (text, size, bold, w) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    return doc.splitTextToSize(String(text == null ? "-" : text), w);
  };
  const widthOf = (text, size, bold) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    return doc.getTextWidth(String(text == null ? "-" : text));
  };
  const put = (text, x, yy, size, { bold, align, maxWidth, color } = {}) => {
    doc.setFont(FONT_NAME, bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...(color || COLOR_TEXT));
    const opts = {};
    if (align) opts.align = align;
    if (maxWidth) opts.maxWidth = maxWidth;
    doc.text(String(text == null ? "-" : text), x, yy, opts);
  };

  /** Verilen metni `w` genişliğinde ve `h` yüksekliğinde kutuya sığdıran en
   *  büyük puntoyu bulur. Tek parçalı uzun değerler (boşluksuz) sarılamaz;
   *  bu yüzden satır genişliği AYRICA kontrol edilir. */
  const fitFontSize = (text, { maxFS, minFS, w, h, bold, lineFactor = 1.12 }) => {
    for (let fs = maxFS; fs >= minFS; fs -= 0.5) {
      const lines = wrap(text, fs, bold, w);
      const widest = Math.max(...lines.map((l) => widthOf(l, fs, bold)));
      if (widest <= w && lines.length * fs * lineFactor <= h) {
        return { fs, lines, lineH: fs * lineFactor };
      }
    }
    const lines = wrap(text, minFS, bold, w);
    return { fs: minFS, lines, lineH: minFS * lineFactor };
  };

  // --- 1) Karekod (sağ üst) ---
  // Konum/raf etiketinde karekod BİLİNÇLİ OLARAK küçük (44pt ≈ 15mm):
  // bu etiketin işi raftan okunmak, karekod ikincil. Küçülen her pt
  // doğrudan raf/konum puntosuna gidiyor. (10 × 10 karekodlu etikette
  // ölçü 62pt olarak KALDI — oraya dokunulmadı.)
  const qrSize = 44;
  const qrX = LABEL_W - margin - qrSize;
  const qrY = margin + 2;
  let hasQr = false;
  try {
    const qrPng = await svgToPng(qrSvgEl, 320);
    if (qrPng) {
      doc.addImage(qrPng, "PNG", qrX, qrY, qrSize, qrSize);
      hasQr = true;
    }
  } catch {
    /* QR gömülemedi — etiket karekodsuz üretilir, diğer bilgiler korunur */
  }

  // --- 2) Üst blok: firma adı / Lastik Oteli / iletişim ---
  const headRight = hasQr ? qrX - 8 : LABEL_W - margin;
  const headW = headRight - margin;
  const headCenter = margin + headW / 2;

  let y = margin + 16;
  const tenantTitle = (tenantName || "").trim();
  if (tenantTitle) {
    const lines = wrap(tenantTitle, 14, true, headW);
    put(lines.join("\n"), headCenter, y, 14, { bold: true, align: "center", maxWidth: headW });
    y += lines.length * 16;
  }
  put("Lastik Oteli", headCenter, y, 12, { bold: true, align: "center" });
  y += 14;
  if (headerContact) {
    const lines = wrap(headerContact, 8.5, false, headW);
    put(lines.join("\n"), headCenter, y, 8.5, { align: "center", maxWidth: headW });
    y += lines.length * 10;
  }

  // Ayraç karekodun ALTINDAN geçer ki iki blok çakışmasın.
  y = Math.max(y + 6, hasQr ? qrY + qrSize + 10 : y + 6);
  doc.setDrawColor(...COLOR_DIVIDER);
  doc.setLineWidth(1.1);
  doc.line(margin, y, LABEL_W - margin, y);
  const dividerY = y;

  // --- 3) En alt satır için yer ayır ---
  // Burada ESKİDEN web sitesi yazıyordu; yerini DEPO bilgisi aldı (aynı
  // konum, aynı punto). Raf/konum için kalan alan bu yükseklik düşülerek
  // hesaplanır, böylece iki blok hiçbir durumda çakışmaz.
  const FOOTER_FS = 8;
  const footerH = 12;
  const contentTop = dividerY;
  const contentBottom = bottomLimit - footerH;
  const availH = contentBottom - contentTop;

  // --- 4) Müşteri adı + plaka + RAF / KONUM ---
  // Alan paylaşımı: isim + plaka en çok %38'ini alır, kalan tamamı
  // raf/konuma gider. Raf/konum ASIL bilgidir; artan boşluk daima ona gider.
  const customerUpper = (storage.customer_name || "-")
    .toLocaleUpperCase("tr-TR").trim();
  const plateText = (storage.plate || "-").toLocaleUpperCase("tr-TR").trim();
  const locationText = (storage.location || "-").toLocaleUpperCase("tr-TR").trim();

  // İsim kutusunun bir bölümü plakaya ayrılır: plaka İSİMLE AYNI PUNTODA
  // yazılacağı için isim fit'i baştan bu satırı hesaba katmalı.
  const nameBoxH = availH * 0.26;
  const name = fitFontSize(customerUpper, {
    maxFS: 19, minFS: 8, w: fullW, h: nameBoxH * 0.62, bold: true,
  });
  const nameBlockH = name.lines.length * name.lineH;

  // DİKKAT: jsPDF'te `y` TABAN ÇİZGİSİDİR. Ayraçtan sonraki ilk satırın üst
  // uzantısı (ascent ≈ punto × 0.78) kadar boşluk bırakılmazsa yazı çizginin
  // üstüne biner (10 × 10 karekodlu etiketteki aynı tuzak).
  let ny = contentTop + name.fs * 0.78 + 8;
  put(name.lines.join("\n"), centerX, ny, name.fs,
      { bold: true, align: "center", maxWidth: fullW });

  const nameBottom = ny - name.fs * 0.78 + nameBlockH;

  // Plaka — müşteri adının HEMEN ALTINDA ve AYNI PUNTODA. Yalnızca satıra
  // sığmıyorsa (çok uzun/bozuk plaka) kendi içinde küçülür.
  let plateFs = name.fs;
  while (plateFs > 8 && widthOf(plateText, plateFs, true) > fullW) plateFs -= 0.5;
  const plateY = nameBottom + plateFs * 0.78 + 3;
  put(plateText, centerX, plateY, plateFs, { bold: true, align: "center" });
  const headBottom = plateY + plateFs * 0.24;

  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.6);
  doc.line(margin, headBottom + 6, LABEL_W - margin, headBottom + 6);

  const locTop = headBottom + 6;
  // "RAF / KONUM" açıklaması BİLİNÇLİ OLARAK küçük: bu satır yalnızca altındaki
  // değerin ne olduğunu söyler, okunması gereken bilgi DEĞERİN KENDİSİDİR.
  // Küçültülerek (9 → 6) ve üstündeki/altındaki boşluklar daraltılarak kazanılan
  // dikey alanın tamamı raf/konum puntosuna aktarılır.
  const LOC_LABEL_FS = 6;
  put("RAF / KONUM", centerX, locTop + LOC_LABEL_FS + 3, LOC_LABEL_FS,
      { bold: true, align: "center", color: COLOR_MUTED });

  const locBoxTop = locTop + LOC_LABEL_FS + 6;
  const locBoxH = contentBottom - locBoxTop;
  // Mümkün olan EN BÜYÜK punto: 160pt'den başlanır (kısa "A-12" gibi
  // değerlerde etiketi dolduracak kadar), uzun değerlerde otomatik düşer.
  // minFS 6: METNİN TAMAMI görünmeli — kısaltma/kırpma YOK, gerekirse satır
  // kaydırılır ve punto bu tabana kadar iner. Satır aralığı 1.08 → 1.02:
  // çok satırlı değerlerde satırlar sıkışır, punto büyür.
  const loc = fitFontSize(locationText, {
    maxFS: 160, minFS: 6, w: fullW, h: locBoxH, bold: true, lineFactor: 1.02,
  });
  const locBlockH = loc.lines.length * loc.lineH;
  // Kalan kutuda dikey ortalama
  const locY = locBoxTop + (locBoxH - locBlockH) / 2 + loc.fs * 0.78;
  put(loc.lines.join("\n"), centerX, locY, loc.fs,
      { bold: true, align: "center", maxWidth: fullW });

  // --- 5) En alt satır: DEPO (web sitesinin yerini aldı) ---
  const warehouseText = (storage.warehouse_name || "").trim();
  if (warehouseText) {
    const caption = `DEPO: ${warehouseText}`;
    // Tek satırda kalmalı: sarma yerine gerekirse hafifçe küçültülür,
    // aksi hâlde ikinci satır sayfanın dışına taşardı.
    let fs = FOOTER_FS;
    while (fs > 5 && widthOf(caption, fs, false) > fullW) fs -= 0.25;
    put(caption, centerX, bottomLimit - 1, fs,
        { align: "center", color: COLOR_MUTED });
  }

  doc.save(`etiket-10x10-raf-${storage.id.slice(0, 8)}.pdf`);
}


/**
 * Emanet (giriş) makbuzu — verilir müşteriye lastik teslim edildiğinde.
 */
export async function generateDepositReceipt({
  storage, tenantName, defaultCurrency, qrSvgEl, contact,
}) {
  const { doc, W, y: startY } = await buildBaseDoc({
    storage,
    tenantName,
    qrSvgEl,
    title: "EMANET MAKBUZU",
    kind: "deposit",
    contact,
  });

  const currencySymbol = CURRENCY_SYMBOL[defaultCurrency] || defaultCurrency;
  let y = startY;
  y = infoRow(doc, y, "Müşteri", storage.customer_name);
  y = infoRow(doc, y, "Telefon", storage.customer_phone);
  if (storage.plate) y = infoRow(doc, y, "Plaka", storage.plate);

  const items = tireItems(storage);
  if (items.length === 1) {
    const it = items[0];
    y = infoRow(doc, y, "Lastik", `${it.brand} - ${it.size}`);
    y = infoRow(doc, y, "Adet / Sezon", `${it.quantity} adet - ${seasonLabel(it.season)}`);
  } else {
    y = itemsBlock(doc, W, y, items);
    const totalQty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
    y = infoRow(doc, y, "Toplam Adet", `${totalQty} adet`);
  }

  // NOT: Depo / Raf makbuzda GÖSTERİLMEZ. Depo yerleşimi işletmenin iç
  // bilgisidir ve emanet süresince değişebilir (Emanet Detayı ekranından
  // güncellenebiliyor) → basılı makbuzdaki değer yanıltıcı olurdu.
  if (items.length === 1 && items[0].dot) y = infoRow(doc, y, "DOT", items[0].dot);
  y = infoRow(doc, y, `Ücret (${currencySymbol})`, fmtMoney(storage.fee, defaultCurrency));
  y = infoRow(doc, y, `Ödenen (${currencySymbol})`, fmtMoney(storage.paid_amount, defaultCurrency));

  y = divider(doc, W, y + 4);

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Bu makbuz, lastiklerinizin tarafımızca emanet alındığına dair belgedir.", 24, y, { maxWidth: W - 48 });
  doc.text("Teslim sırasında bu makbuzu veya QR kodu ibraz ediniz.", 24, y + 12, { maxWidth: W - 48 });
  doc.text("Bir yıl içinde teslim alınmayan emanetlerden firmamız sorumlu değildir.", 24, y + 24, { maxWidth: W - 48 });
  doc.text("Lastiklerinizi teslim almak için en az 3 gün önceden randevu alınız.", 24, y + 36, { maxWidth: W - 48 });  

  y += 38;
  doc.setDrawColor(...COLOR_BORDER);
  doc.line(24, y, 200, y);
  doc.line(W - 200, y, W - 24, y);
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Emaneti Alan", 24, y + 12);
  doc.text("Teslim Eden (Müşteri)", W - 200, y + 12, { maxWidth: 120, align: "left" });

  doc.save(`emanet-makbuzu-${storage.id.slice(0, 8)}.pdf`);
}

/**
 * Teslim (çıkış) makbuzu — verilir lastik müşteriye geri teslim edilirken.
 */
export async function generateDeliveryReceipt({
  storage, tenantName, defaultCurrency, qrSvgEl, contact,
}) {
  const { doc, W, y: startY } = await buildBaseDoc({
    storage,
    tenantName,
    qrSvgEl,
    title: "TESLİM MAKBUZU",
    kind: "delivery",
    contact,
  });

  const currencySymbol = CURRENCY_SYMBOL[defaultCurrency] || defaultCurrency;
  let y = startY;
  y = infoRow(doc, y, "Müşteri", storage.customer_name);
  y = infoRow(doc, y, "Telefon", storage.customer_phone);
  if (storage.plate) y = infoRow(doc, y, "Plaka", storage.plate);

  const items = tireItems(storage);
  if (items.length === 1) {
    const it = items[0];
    y = infoRow(doc, y, "Lastik", `${it.brand} - ${it.size}`);
    y = infoRow(doc, y, "Adet / Sezon", `${it.quantity} adet - ${seasonLabel(it.season)}`);
  } else {
    y = itemsBlock(doc, W, y, items);
    const totalQty = items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
    y = infoRow(doc, y, "Toplam Adet", `${totalQty} adet`);
  }

  // Depo bilgisi teslim makbuzunda GÖSTERİLMEZ (bkz. emanet makbuzu notu).
  y = infoRow(doc, y, "Giriş Tarihi", fmtDate(storage.check_in_date));
  y = infoRow(doc, y, "Teslim Tarihi", fmtDate(storage.check_out_date));
  y = infoRow(doc, y, `Toplam Ücret (${currencySymbol})`, fmtMoney(storage.fee, defaultCurrency));
  y = infoRow(doc, y, `Ödenen Tutar (${currencySymbol})`, fmtMoney(storage.paid_amount, defaultCurrency));

  const remaining = Math.max(0, (storage.fee || 0) - (storage.paid_amount || 0));
  if (remaining > 0) {
    doc.setFillColor(...COLOR_BG);
    doc.rect(20, y - 6, W - 40, 28, "F");
    doc.setFontSize(10);
    doc.setFont(FONT_NAME, "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(`KALAN TUTAR: ${fmtMoney(remaining, defaultCurrency)}`, 28, y + 12);
    y += 36;
  }

  y = divider(doc, W, y + 4);

  doc.setFont(FONT_NAME, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_MUTED);
  doc.text(
    "Yukarıda belirtilen lastikler tarafıma eksiksiz olarak teslim edilmiştir.",
    24,
    y,
    { maxWidth: W - 48 }
  );
  doc.text("Hiçbir hak ve alacak iddia etmeyeceğimi beyan ederim.", 24, y + 12, { maxWidth: W - 48 });

  y += 44;
  doc.setDrawColor(...COLOR_BORDER);
  doc.line(24, y, 200, y);
  doc.line(W - 200, y, W - 24, y);
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Teslim Eden (Yetkili)", 24, y + 12);
  doc.text("Teslim Alan (Müşteri)", W - 200, y + 12, { maxWidth: 120, align: "left" });

  doc.save(`teslim-makbuzu-${storage.id.slice(0, 8)}.pdf`);
}
